import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";


import appCss from "../styles.css?url";
import { AuthProvider } from "@/hooks/use-auth";
import { Toaster } from "@/components/ui/sonner";
import { CookieNotice } from "@/components/cookie-notice";
import { AppErrorBoundary } from "@/components/error-boundary";
import { initSentry } from "@/lib/sentry";
import { Sentry } from "@/lib/sentry";
import { installGlobalErrorHandlers } from "@/lib/global-handlers";
import { initAnalytics } from "@/lib/analytics";
import { UpdateBanner } from "@/components/update-banner";
import { PersistentNav, PullIndicator } from "@/components/app-shell";

if (typeof window !== "undefined") {
  initSentry();
  installGlobalErrorHandlers();
  initAnalytics();
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();

  return (
    <div
      className="flex min-h-screen items-center justify-center px-6"
      style={{ backgroundColor: "var(--caileve-bg)" }}
    >
      <div className="max-w-md text-center">
        <h1
          className="text-3xl"
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            color: "var(--caileve-lilac)",
          }}
        >
          Something went wrong.
        </h1>
        <p
          className="mt-4 text-base leading-relaxed"
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            color: "var(--caileve-ink)",
          }}
        >
          Tap to reload.
        </p>
        <button
          type="button"
          onClick={() => {
            router.invalidate();
            reset();
            if (typeof window !== "undefined") window.location.reload();
          }}
          className="mt-6 px-6 py-2.5 text-xs uppercase tracking-[0.25em] text-[var(--caileve-bg)]"
          style={{ backgroundColor: "var(--caileve-lilac)" }}
        >
          Reload
        </button>
        <p
          className="mt-6 text-sm"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-ink)" }}
        >
          Need help?{" "}
          <a href="mailto:hello@caileve.app" style={{ color: "var(--caileve-lilac-text)" }} className="underline">
            hello@caileve.app
          </a>
        </p>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0, viewport-fit=cover" },
      { title: "Cailève" },
      { name: "description", content: "Your personal styling concierge. Organize jewelry, build curated looks, and receive elegant styling suggestions tailored to your wardrobe, mood, and moment." },
      { name: "author", content: "Écrin Style LLC" },
      { property: "og:title", content: "Cailève" },
      { property: "og:description", content: "The only app built around the pieces you love most. Organize your jewelry, build complete looks, and receive styling made for your collection." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@caileve" },
      { name: "twitter:title", content: "Cailève" },
      { name: "twitter:description", content: "The only app built around the pieces you love most. Organize your jewelry, build complete looks, and receive styling made for your collection." },
      { property: "og:image", content: "https://caileve.app/caileve_social_image.webp" },
      { name: "twitter:image", content: "https://caileve.app/caileve_social_image.webp" },
      { name: "theme-color", content: "var(--caileve-bg)" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "Cailève" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap",
      },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192x192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512x512.png" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RouteTransition() {
  return <Outlet />;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    const saved = localStorage.getItem("caileve_dark_mode") === "1";
    if (saved) {
      document.documentElement.classList.add("dark");
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute("content", "#1A1020");
    } else {
      document.documentElement.classList.remove("dark");
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute("content", "#F8F5FA");
    }
  }, []);

  useEffect(() => {
    const unsub = router.subscribe("onResolved", (event) => {
      try {
        const toPath = event.toLocation?.pathname ?? "";
        // Parameterize UUIDs so transactions group as /jewelry/:id, not raw URLs.
        const txName = toPath.replace(
          /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
          ":id",
        );
        Sentry.getCurrentScope().setTransactionName(txName);
        Sentry.addBreadcrumb({
          category: "navigation",
          level: "info",
          message: `${event.fromLocation?.pathname ?? ""} -> ${event.toLocation?.pathname ?? ""}`,
          data: {
            from: event.fromLocation?.pathname,
            to: event.toLocation?.pathname,
          },
        });
      } catch {
        // ignore
      }
    });
    return () => unsub();
  }, [router]);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

    let isInIframe = false;
    try {
      isInIframe = window.self !== window.top;
    } catch {
      isInIframe = true;
    }
    const host = window.location.hostname;
    const isPreviewHost =
      host.includes("id-preview--") ||
      host.includes("lovableproject.com") ||
      host.includes("lovable.dev");

    if (isInIframe || isPreviewHost) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => r.unregister());
      });
      return;
    }

    navigator.serviceWorker.register("/sw.js").catch(() => {
      // ignore registration errors
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppErrorBoundary>
        <AuthProvider>
          <RouteTransition />
          <PullIndicator />
          <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 40 }}>
            <PersistentNav />
          </div>
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                backgroundColor: "var(--caileve-bg)",
                color: "var(--caileve-ink)",
                border: "1px solid var(--caileve-soft)",
                borderRadius: 4,
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: 14,
              },
            }}
          />
          <CookieNotice />
        </AuthProvider>
      <UpdateBanner />
      </AppErrorBoundary>
    </QueryClientProvider>
  );
}
